import ContentCard from 'components/ContentCard';
import React from 'react';

import { vw } from 'pages/affiliatePartner/AffiliatePartner.style';
import {
  CharterSection,
  H3Text,
  ImageOverlay,
} from 'pages/charterQuote/components/contents/Content.style';
import Testimonial from 'pages/home/components/Testimonial';
import styled from 'styled-components';

const ListContent = styled(CharterSection)`
  padding: 0 ${vw(100)};
  margin: 64px 0;
  @media (max-width: 938px) {
    padding: 0;
    margin: 16px 0;
  }
`;

const StepList = styled.ul`
  margin: 16px 0;
  list-style: initial;
  & > li {
    margin: 12px 0;

    &::marker {
      color: var(--color-theme);
    }
  }
`;

const SubTitle = styled.h2`
  color: var(--color-theme);
`;

const InitialText = styled.span`
  font-weight: var(--fw-bold);
  color: var(--color-theme);
  margin-right: 8px;
`;

const MainContainer = styled.div`
  padding: 32px;

  font-size: var(--fs-500);
  h3 {
    font-size: var(--fs-800);
    margin: 0;
  }
  h2 {
    font-size: var(--fs-900);
    margin: 0;
  }
  @media (max-width: 938px) {
    padding: 16px;
  }
`;

const steps = [
  {
    text: 'Cost Savings:',
    subText:
      'Empty leg jet flights allow you to experience private travel at a 75% discount over standard prices.',
  },
  {
    text: 'Environmental Impact:',
    subText:
      "Booking an empty leg flight offers incredible cost savings and helps reduce the environmental impact of repositioning flights. By filling these legs, you're contributing to a more sustainable approach to private air travel.",
  },
  {
    text: 'Exclusive Access:',
    subText:
      'Enjoy the privacy and comfort of a private jet without sharing the cabin with strangers.',
  },
];

const FAQOverlay = styled(ImageOverlay)`
  text-align: left;
  min-height: 200px;
`;

const FAQList = styled.ul`
  padding: 12px;

  & > li {
    margin: 12px 0;
  }
`;

const FAQ = () => {
  return (
    <CharterSection id="faq-emptyLeg">
      <FAQOverlay $img="imgs/webp/charterQuote/business-jet-closeup.webp">
        <h2>FAQs About Empty Leg Flights</h2>
        <p>Do you have more questions about empty leg flights? Airacer has answers.</p>
      </FAQOverlay>
      <FAQList>
        <li>
          <H3Text>What Determines the Cost of Empty Leg Charter Flights?</H3Text>
          Pricing for empty leg charter flight depends on the route, aircraft type, and
          availability. These factors mean significant savings compared to regular private jet
          bookings.
        </li>

        <li>
          <H3Text>How Flexible Are Empty Leg Flights?</H3Text>
          Empty leg jet flights require some flexibility. While schedules may change, the benefits
          of affordability and luxury make them an attractive choice for travelers with flexible
          schedules.
        </li>

        <li>
          <H3Text>Are Taxes, Fees, and other Costs Included in the Price?</H3Text>
          No, final costs cannot be calculated until operators and FBO are contacted. These rates
          are net prices and are subject to taxes, regulatory fees, or other charges, which may vary
          based on routing and availability
        </li>

        <li>
          <H3Text>Can I use Airacer Credits to purchase Empty Leg flights?</H3Text>
          No. Airacer Credits cannot be applied toward Empty Leg flights unless expressly approved
          by Airacer in writing.
        </li>
      </FAQList>
    </CharterSection>
  );
};

const booking = [
  {
    text: 'Limited Availability:',
    subText:
      'Compared to commercial airlines or booking a private flight yourself, there are fewer options when you’re trying to fly on an empty leg.',
  },
  {
    text: 'Flexible Schedules Required:',
    subText:
      'Flight times or destinations may mean that a flight leaves earlier or later than you might prefer.',
  },
  {
    text: 'Act Fast:',
    subText:
      'Because empty leg flights are available at discounted rates, they often sell out quickly. You need to be ready to book a flight soon after it is listed to make sure you claim your seat.',
  },
];

const Procedures = () => {
  return (
    <ListContent>
      <SubTitle>Benefits of Empty Leg Flights</SubTitle>
      <p>
        By taking advantage of empty leg private flights, travelers can enjoy the luxury,
        exclusivity, and comfort of private jet travel at a significant discount.
      </p>
      <StepList>
        {steps.map((step) => {
          return (
            <li key={step.text}>
              <InitialText>{step.text}</InitialText>
              {step.subText}
            </li>
          );
        })}
      </StepList>
    </ListContent>
  );
};

// styled component example container with three columns equally
const ExamplesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  justify-content: space-between;
  margin: 32px 0;

  text-align: center;
  gap: 8px;
  @media (max-width: 938px) {
    gap: 16px;
  }
`;

const ExampleContainer = styled.div`
  border-radius: 32px;
  background-color: var(--bgck-light-purple);
  padding: 16px;
  color: white;
  & > h4 {
    color: white;
  }
`;

const StyledImg = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
`;

const StyledArticle = styled.article`
  padding: 24px;
  display: grid;
  place-items: center;
  text-align: center;
  margin: 24px;
`;

const GetMoreLink = styled.a`
  display: block;
  margin: 32px auto;
  width: fit-content;
  padding: 8px 24px;
  border: 1.5px solid rgba(13, 17, 35, 0.48);
  color: black;
  border-radius: 8px;
  font-weight: 700;
  font-size: 24px;
  text-align: center;
  cursor: pointer;
  &:hover {
    color: #ffffff;
    border-color: transparent;
    background-color: rgba(13, 17, 35, 0.48);
  }
  &:active {
    color: #ffffff;
    border-color: transparent;
    background-color: #07080a;
  }
`;

const Examples = () => {
  return (
    <div style={{ textAlign: 'center' }}>
      <ExamplesContainer>
        <ExampleContainer>
          <h4>Toronto to Singapore on a Gulfstream G650</h4>
          <p>Normal price: $260,000-$290,000</p>
          Empty leg price: $160,000
        </ExampleContainer>

        <ExampleContainer>
          <h4>New York to Los Angeles on a Gulfstream GIVSP</h4>
          <p>Normal price: $53,000-56,000</p>
          Empty leg price: $30,000
        </ExampleContainer>

        <ExampleContainer>
          <h4>Los Angeles to Las Vegas on a Hawker 400XP</h4>
          <p>Normal price: $8,900</p>
          Empty leg price: $4,800
        </ExampleContainer>
      </ExamplesContainer>
      <p>
        Why Customers Love Airacer’s Empty Leg Flights At Airacer, we’re proud to deliver more than
        empty-leg flights—we offer an experience defined by affordability, luxury, and exceptional
        service.
      </p>
      <GetMoreLink href="/charterQuote" target="_blank">
        private jet charter quote
      </GetMoreLink>
    </div>
  );
};

const Description = () => {
  return (
    <MainContainer>
      <section id="what-emptyLeg">
        <ContentCard
          item={{
            picture: 'imgs/webp/emptyLeg/passenger-jet-takeoff.webp',
            altImage:
              'A commercial passenger jet on the runway, about to take off against a backdrop of a blue sky with some clouds.',
          }}
        >
          <h2 className="title">What Are Empty Leg Flights?</h2>

          <p>
            Empty legs on private jets are pre-scheduled return trips for private aircraft that
            would otherwise fly empty. These flights become available when a private jet completes
            its one-way charter and must return to its base or head to its next pick-up location.
          </p>
          <p>
            Airacer specializes in providing access to these flights, making private aviation more
            affordable and accessible.
          </p>
        </ContentCard>
      </section>
      <Procedures />

      <ImageOverlay $img="imgs/webp/emptyLeg/airport-sunset.webp">
        <h3>Things to Know Before Booking an Empty Leg Flight</h3>
        <p>
          While the privacy, exclusivity, and luxury of an empty leg flight are exciting, there are
          a few things to keep in mind when you’re looking to book a flight on a private jet. 
        </p>
      </ImageOverlay>

      <ListContent>
        <StepList>
          {booking.map((step) => {
            return (
              <li key={step.text}>
                <InitialText>{step.text}</InitialText>
                {step.subText}
              </li>
            );
          })}
        </StepList>
        <p>
          Ready to explore affordable luxury? Use Airacer’s simple search tool to find empty leg
          flights today!
        </p>
      </ListContent>
      <CharterSection id="find-emptyLeg">
        <StyledImg src="imgs/webp/emptyLeg/private-jet-wing.webp" alt="private jet on flight." />

        <StyledArticle>
          <SubTitle>Find Empty Leg Flights</SubTitle>
          <p>
            Finding and booking empty leg flights with Airacer is easy. Our platform offers a
            comprehensive search tool that lets you explore a variety of routes and aircraft options
            for traveling via empty leg private jets.
          </p>
        </StyledArticle>
      </CharterSection>
      <CharterSection>
        <ContentCard
          item={{
            picture: 'imgs/webp/emptyLeg/private-jet-interior.webp',
            altImage:
              'interior view of a private jet with plush seating, wooden tables, and decorative accents.',
            reverse: true,
          }}
        >
          <h2 className="title">How to Use the Search Tool</h2>

          <p>
            Our search tool is designed to make it easy for you to find empty leg flights. Follow
            these few simple steps to find and book a flight today:
          </p>

          <StepList>
            <li>
              Enter Your Preferences: Input your preferred dates, destination, and any specific
              requirements.
            </li>
            <li>
              Browse Available Flights: View a curated list of empty leg jet charter options
              tailored to your needs.
            </li>
            <li>
              Book Your Flight: Complete your reservation through the platform for a hassle-free
              experience.
            </li>
          </StepList>
        </ContentCard>
      </CharterSection>

      <CharterSection>
        <ImageOverlay $img="imgs/webp/emptyLeg/private-jet-exterior.webp">
          <h2>Example Routes and Savings</h2>
          <p>
            Wondering how much you could save with empty leg flights? Here are some example routes
            showcasing the incredible value Airacer provides:
          </p>
        </ImageOverlay>
        <Examples />
      </CharterSection>
      <Testimonial />

      <FAQ />
    </MainContainer>
  );
};

export default Description;
